import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, MapPin } from 'lucide-react';
import SectionLabel from '../components/SectionLabel';
import BookingCTA from '../components/BookingCTA';
import SEO from '../components/SEO';
import { serviceSchema, breadcrumbSchema } from '../lib/schema';
import { useLang } from '../context/LanguageContext';

const HOME_SLUGS = ['detailing'];

const ServiceDetail = () => {
  const { slug } = useParams();
  const { c, ui, lp } = useLang();
  const t = ui.serviceDetail;
  const service = c.services.find(s => s.slug === slug);

  if (!service) {
    return (
      <div className="bg-[#0a0a0b] text-white pt-32 pb-24 min-h-screen">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <h1 className="text-3xl">{t.notFound}</h1>
          <Link to={lp('diensten')} className="text-[#2f6bff] mt-4 inline-block">{t.back}</Link>
        </div>
      </div>
    );
  }

  const isHome = HOME_SLUGS.includes(service.slug);
  const relatedSlug = service.slug === 'lakcorrectie' ? 'glascoating' : 'lakcorrectie';
  const related = c.services.find(s => s.slug === relatedSlug);

  return (
    <div className="bg-[#0a0a0b] text-white pt-32 pb-24">
      <SEO
        path={`/diensten/${service.slug}`}
        title={service.seoTitle || `${service.title} — ${t.seoSuffix}`}
        description={service.seoDesc || `${service.description} ${t.seoDescRegion}`}
        image={service.image}
        jsonLd={[
          serviceSchema({
            name: service.title,
            description: service.description,
            image: service.image,
            price: service.price,
            slug: service.slug,
          }),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Diensten', path: '/diensten' },
            { name: service.title, path: `/diensten/${service.slug}` },
          ]),
        ]}
      />
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <Link to={lp('diensten')} className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-10 transition-colors">
          <ArrowLeft className="w-4 h-4" /> {t.allServices}
        </Link>
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="relative rounded-2xl overflow-hidden aspect-[4/5] lg:sticky lg:top-28 self-start">
            <img src={service.image} alt={`${service.title} — Shiny-Cleaning Deventer`} className="w-full h-full object-cover" />
          </div>
          <div>
            <SectionLabel>{t.serviceLabel} {service.number}</SectionLabel>
            <h1 className="mt-5 text-4xl md:text-6xl font-medium tracking-tight leading-[1.05]">{service.title}</h1>
            <p className="mt-6 text-white/70 text-lg">{service.short}</p>
            <div className="mt-8 inline-block px-5 py-3 rounded-md border border-white/10 bg-white/5 text-white">{service.price}</div>

            <h2 className="mt-12 text-2xl font-medium">{t.whatTitle}</h2>
            <p className="mt-4 text-white/70 leading-relaxed">{service.description}</p>

            <h2 className="mt-10 text-2xl font-medium">{t.forWhomTitle}</h2>
            <ul className="mt-4 space-y-3">
              {service.suitableFor.map(item => (
                <li key={item} className="flex items-start gap-3 text-white/85">
                  <span className="mt-1 w-5 h-5 rounded-full bg-[#2f6bff]/20 text-[#2f6bff] flex items-center justify-center flex-shrink-0"><Check className="w-3 h-3" /></span>
                  {item}
                </li>
              ))}
            </ul>

            {service.benefits && (
              <>
                <h2 className="mt-10 text-2xl font-medium">{t.benefitsTitle}</h2>
                <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="service-benefits">
                  {service.benefits.map(item => (
                    <li key={item} className="flex items-start gap-3 text-white/85">
                      <span className="mt-1 w-5 h-5 rounded-full bg-[#2f6bff]/20 text-[#2f6bff] flex items-center justify-center flex-shrink-0"><Check className="w-3 h-3" /></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {service.expectation && (
              <div className="mt-10 p-6 rounded-xl border border-white/10 bg-white/[0.02]" data-testid="service-expectation">
                <h2 className="text-xl font-medium">{t.expectationTitle}</h2>
                <p className="mt-3 text-white/70 text-sm leading-relaxed">{service.expectation}</p>
              </div>
            )}

            <div className="mt-10 p-6 rounded-xl border border-white/10 bg-white/[0.02]">
              <div className="flex items-center gap-2 text-[#2f6bff] text-xs tracking-[0.25em] uppercase">
                <MapPin className="w-4 h-4" /> {t.regionTitle}
              </div>
              <p className="mt-3 text-white/70 text-sm leading-relaxed">{t.regionText}</p>
              <p className="mt-2 text-white/60 text-sm leading-relaxed">{isHome ? t.regionHome : t.regionWorkshop}</p>
            </div>

            <div className="mt-10">
              <BookingCTA waMessage={t.waQuote(service.title)} offerteTreatment={service.title} />
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
              <span className="text-white/40">{t.linksLabel}:</span>
              {isHome && <Link to={lp('mobile')} className="text-[#7aa3ff] hover:text-white transition-colors">{c.nav.find(n => n.key === 'mobile')?.label}</Link>}
              {related && <Link to={lp('service', { slug: related.slug })} className="text-[#7aa3ff] hover:text-white transition-colors">{related.title}</Link>}
              <Link to={lp('prijzen')} className="text-[#7aa3ff] hover:text-white transition-colors">{ui.mobile.linkPricing}</Link>
              <Link to={lp('portfolio')} className="text-[#7aa3ff] hover:text-white transition-colors">{ui.mobile.linkProjects}</Link>
              <Link to={lp('contact')} className="inline-flex items-center gap-1 text-[#7aa3ff] hover:text-white transition-colors">{ui.contact.label} <ArrowRight className="w-3.5 h-3.5" /></Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetail;
